import { vec3 } from 'gl-matrix';
import { DicomImageSlice } from '~/domain/dicom/dicomImageSlice';
import { computeSliceAABB } from './assembleVolume';

export interface ProjectionResult {
    /** Ready for canvas putImageData */
    imageData: ImageData;
    widthPx: number;
    heightPx: number;
    /** AABB min corner in world space (mm) */
    worldMin: Readonly<vec3>;
    /** AABB max corner in world space (mm) */
    worldMax: Readonly<vec3>;
    /** Scale: mm per output pixel */
    mmPerPixel: number;
}

/**
 * Compute auto window/level from a MIP buffer using percentile
 * clamping. Ignores background pixels (-Infinity).
 *
 * Returns [windowCenter, windowWidth] suitable for contrast mapping.
 */
function autoWindowFromBuffer(buffer: Float32Array): [number, number] {
    // Collect non-background values
    const values: number[] = [];
    for (let i = 0; i < buffer.length; i++) {
        if (buffer[i] !== -Infinity) {
            values.push(buffer[i]);
        }
    }

    if (values.length === 0) {
        return [0, 400];
    }

    values.sort((a, b) => a - b);

    // Use 1st and 99th percentiles to avoid outlier clipping
    const lo = values[Math.floor(values.length * 0.01)];
    const hi = values[Math.floor(values.length * 0.99)];

    const ww = Math.max(1, hi - lo);
    const wc = lo + ww / 2;
    return [wc, ww];
}

/**
 * Render a Maximum Intensity Projection (MIP) of raw DICOM slices
 * along one of three orthographic axes.
 *
 * The algorithm iterates each slice once, projecting pixel values
 * onto the output plane and keeping the maximum value at each output
 * pixel. Contrast is auto-computed from the resulting MIP buffer
 * using percentile-based windowing.
 *
 * @param slices         Array of DicomImageSlice objects
 * @param axis           Projection axis
 * @param maxOutputSize  Maximum dimension of the output image (px)
 * @returns ProjectionResult with ImageData for direct canvas rendering
 */
export function renderMIPProjection(
    slices: DicomImageSlice[],
    axis: 'coronal' | 'sagittal' | 'axial',
    maxOutputSize: number
): ProjectionResult {
    if (slices.length === 0) {
        throw new Error('No slices provided for MIP projection');
    }

    const { worldMin, worldMax } = computeSliceAABB(slices);

    // Compute extent along each world axis
    const extentX = worldMax[0] - worldMin[0];
    const extentY = worldMax[1] - worldMin[1];
    const extentZ = worldMax[2] - worldMin[2];

    // Determine output plane dimensions based on projection axis
    // coronal (AP): projects onto XZ plane
    // sagittal (LAT): projects onto YZ plane
    // axial (SUP): projects onto XY plane
    let planeW: number;
    let planeH: number;

    switch (axis) {
        case 'coronal':
            planeW = extentX;
            planeH = extentZ;
            break;
        case 'sagittal':
            planeW = extentY;
            planeH = extentZ;
            break;
        case 'axial':
            planeW = extentX;
            planeH = extentY;
            break;
    }

    // Compute output resolution
    const maxExtent = Math.max(planeW, planeH);
    if (maxExtent <= 0) {
        throw new Error('Invalid AABB extent for projection');
    }
    const mmPerPixel = maxExtent / maxOutputSize;

    const outW = Math.max(1, Math.ceil(planeW / mmPerPixel));
    const outH = Math.max(1, Math.ceil(planeH / mmPerPixel));

    // Accumulation buffer (float, stores max HU values)
    const mipBuffer = new Float32Array(outW * outH);
    mipBuffer.fill(-Infinity);

    // Compute median inter-slice spacing so each voxel can be
    // splatted across its physical footprint. Without splatting,
    // output pixels between slices are never written, producing
    // visible stripes when slice spacing exceeds mmPerPixel.
    //
    // Project each slice's imagePosition onto the slice normal
    // (cross product of row and column directions) so spacing
    // is correct for gantry-tilted and oblique acquisitions.
    let sliceSpacing = mmPerPixel;
    if (slices.length >= 2) {
        const orient0 = slices[0].imageOrientation;
        const row0 = vec3.fromValues(orient0[0], orient0[1], orient0[2]);
        const col0 = vec3.fromValues(orient0[3], orient0[4], orient0[5]);
        const sliceNormal = vec3.create();
        vec3.cross(sliceNormal, row0, col0);
        vec3.normalize(sliceNormal, sliceNormal);

        const projections = slices.map((s) => {
            const ip = s.imagePosition;
            return (
                (ip[0] as number) * sliceNormal[0] +
                (ip[1] as number) * sliceNormal[1] +
                (ip[2] as number) * sliceNormal[2]
            );
        });
        projections.sort((a, b) => a - b);

        const spacings: number[] = [];
        for (let i = 1; i < projections.length; i++) {
            const d = projections[i] - projections[i - 1];
            if (d > 1e-3) {
                spacings.push(d);
            }
        }
        if (spacings.length > 0) {
            spacings.sort((a, b) => a - b);
            sliceSpacing = spacings[Math.floor(spacings.length / 2)];
        }
    }

    // Process each slice
    for (const slice of slices) {
        const pos = slice.imagePosition;
        const orientation = slice.imageOrientation;
        const dx = slice.pixelSpacing[0];
        const dy = slice.pixelSpacing[1];
        const nx = slice.cols;
        const ny = slice.rows;

        // Row and column direction vectors
        const rowDir = vec3.fromValues(
            orientation[0],
            orientation[1],
            orientation[2]
        );
        const colDir = vec3.fromValues(
            orientation[3],
            orientation[4],
            orientation[5]
        );

        // Get pixel data (Int16 for CT/MR)
        let pixelData: Int16Array;
        try {
            pixelData = slice.pixelData;
        } catch {
            // Skip slices where pixel data is not available (e.g. PET)
            continue;
        }

        // Compute voxel footprint half-extents projected onto
        // the output plane (Minkowski sum of in-plane pixel
        // extent and inter-slice extent along the slice normal).
        const normal = vec3.create();
        vec3.cross(normal, rowDir, colDir);

        let halfExtU: number;
        let halfExtV: number;

        switch (axis) {
            case 'coronal':
                halfExtU =
                    (Math.abs(rowDir[0]) * dx +
                        Math.abs(colDir[0]) * dy +
                        Math.abs(normal[0]) * sliceSpacing) /
                    2;
                halfExtV =
                    (Math.abs(rowDir[2]) * dx +
                        Math.abs(colDir[2]) * dy +
                        Math.abs(normal[2]) * sliceSpacing) /
                    2;
                break;
            case 'sagittal':
                halfExtU =
                    (Math.abs(rowDir[1]) * dx +
                        Math.abs(colDir[1]) * dy +
                        Math.abs(normal[1]) * sliceSpacing) /
                    2;
                halfExtV =
                    (Math.abs(rowDir[2]) * dx +
                        Math.abs(colDir[2]) * dy +
                        Math.abs(normal[2]) * sliceSpacing) /
                    2;
                break;
            case 'axial':
                halfExtU =
                    (Math.abs(rowDir[0]) * dx +
                        Math.abs(colDir[0]) * dy +
                        Math.abs(normal[0]) * sliceSpacing) /
                    2;
                halfExtV =
                    (Math.abs(rowDir[1]) * dx +
                        Math.abs(colDir[1]) * dy +
                        Math.abs(normal[1]) * sliceSpacing) /
                    2;
                break;
        }

        // Convert half-extents to output pixel units
        const splatHalfU = halfExtU / mmPerPixel;
        const splatHalfV = halfExtV / mmPerPixel;

        // For each pixel in the slice, project to output plane
        for (let iy = 0; iy < ny; iy++) {
            // Compute world position for start of row
            const rowStartX = (pos[0] as number) + iy * colDir[0] * dy;
            const rowStartY = (pos[1] as number) + iy * colDir[1] * dy;
            const rowStartZ = (pos[2] as number) + iy * colDir[2] * dy;

            for (let ix = 0; ix < nx; ix++) {
                const wx = rowStartX + ix * rowDir[0] * dx;
                const wy = rowStartY + ix * rowDir[1] * dx;
                const wz = rowStartZ + ix * rowDir[2] * dx;

                const val = pixelData[iy * nx + ix];

                // Map world position to output pixel
                let ox: number;
                let oy: number;

                switch (axis) {
                    case 'coronal':
                        ox = (wx - worldMin[0]) / mmPerPixel;
                        // Z increases downward in output
                        oy = (worldMax[2] - wz) / mmPerPixel;
                        break;
                    case 'sagittal':
                        ox = (wy - worldMin[1]) / mmPerPixel;
                        oy = (worldMax[2] - wz) / mmPerPixel;
                        break;
                    case 'axial':
                        ox = (wx - worldMin[0]) / mmPerPixel;
                        oy = (wy - worldMin[1]) / mmPerPixel;
                        break;
                }

                // Splat voxel to all output pixels within its
                // physical footprint
                const xLo = Math.max(0, Math.floor(ox - splatHalfU));
                const xHi = Math.min(outW - 1, Math.floor(ox + splatHalfU));
                const yLo = Math.max(0, Math.floor(oy - splatHalfV));
                const yHi = Math.min(outH - 1, Math.floor(oy + splatHalfV));

                for (let sy = yLo; sy <= yHi; sy++) {
                    const rowBase = sy * outW;
                    for (let sx = xLo; sx <= xHi; sx++) {
                        const idx = rowBase + sx;
                        if (val > mipBuffer[idx]) {
                            mipBuffer[idx] = val;
                        }
                    }
                }
            }
        }
    }

    // Auto-compute window/level from the MIP buffer
    const [autoWc, autoWw] = autoWindowFromBuffer(mipBuffer);

    // Apply window/level and convert to RGBA ImageData
    const imageData = new ImageData(outW, outH);
    const data = imageData.data;
    const wMin = autoWc - autoWw / 2;
    const wMax = autoWc + autoWw / 2;
    const wRange = wMax - wMin;

    for (let i = 0; i < mipBuffer.length; i++) {
        let val = mipBuffer[i];
        if (val === -Infinity) {
            val = wMin; // background
        }

        // Apply window/level
        let normalized: number;
        if (wRange <= 0) {
            normalized = 0;
        } else if (val <= wMin) {
            normalized = 0;
        } else if (val >= wMax) {
            normalized = 255;
        } else {
            normalized = ((val - wMin) / wRange) * 255;
        }

        const byte = Math.round(normalized);
        const pixIdx = i * 4;
        data[pixIdx] = byte; // R
        data[pixIdx + 1] = byte; // G
        data[pixIdx + 2] = byte; // B
        data[pixIdx + 3] = 255; // A
    }

    return {
        imageData,
        widthPx: outW,
        heightPx: outH,
        worldMin: vec3.clone(worldMin),
        worldMax: vec3.clone(worldMax),
        mmPerPixel,
    };
}
